import React, { useState, useEffect } from 'react';
import { Card, CardBody, Button, Chip, Progress, useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea } from '@nextui-org/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faEye, faClock, faExclamationTriangle, faGripVertical, faTable, faSearch, faRefresh, faCalendarAlt, faUser, faFlag, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getDataWithFilterUrl, getOnedata, UpdateOneData } from '../../controllers/APIController';

import BreadCrumbsPage from '../../components/BreadCrumbsPage';
import LoadingPage from '../../components/Loader/LoadingPage';

/**
 * ApprovalDashboard Component
 * Shows all metrics that need approval/review for the current user
 * Provides a unified interface for managing workflow approvals
 */
const ApprovalDashboard = () => {
  const [pendingItems, setPendingItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // UI State
  const [layoutMode, setLayoutMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [workflowFilter, setWorkflowFilter] = useState("all");
  
  // Modal states
  const { isOpen: isApprovalModalOpen, onOpen: onApprovalModalOpen, onClose: onApprovalModalClose } = useDisclosure();
  
  // Get user information
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const currentUserId = userLogged?.user?.id;
  const userRole = userLogged?.user?.userRole;
  const orgId = userLogged?.user?.LastOrgId;

  useEffect(() => {
    if (currentUserId && orgId) {
      fetchPendingWorkflowItems();
    }
  }, [currentUserId, orgId]);

  useEffect(() => {
    if (pendingItems.length > 0) {
      applyFilters();
    }
  }, [pendingItems, searchTerm, statusFilter, actionFilter, workflowFilter]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        setFiltering(true);
        applyFilters();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Fetch metrics that need approval/review from the current user
   */
  const fetchPendingWorkflowItems = async () => {
    try {
      setIsLoading(true);
      
      // Get responses that need approval/review
      const responses = await getDataWithFilterUrl("responses", 
        `filters[Organization][id][$eqi]=${orgId}&filters[Status][$in]=Created,Approved`
      );
      
      if (!responses?.data) {
        setPendingItems([]);
        setIsLoading(false);
        return;
      }
      
      // Get metric responses linked to these tasks
      const pendingMetrics = await Promise.all(
        responses.data.map(async (response) => {
          try {
            // Get the metric for this response
            const metricResponse = await getDataWithFilterUrl("metrics", 
              `filters[Name][$eqi]=${response.attributes.Metric_Id}`
            );
            
            if (!metricResponse?.data?.[0]) return null;
            
            const metric = metricResponse.data[0];
            
            // Check if current user is approver/reviewer for this metric
            const isApprover = metric.attributes.ApproverTo?.data?.some(
              approver => approver.id === currentUserId
            );
            const isReviewer = metric.attributes.ReviewerTo?.data?.some(
              reviewer => reviewer.id === currentUserId
            );
            
            // Only show items where current user has approval/review role
            if (!isApprover && !isReviewer && userRole !== 'Admin') {
              return null;
            }
            
            // Determine what action is needed
            let actionNeeded = '';
            let canTakeAction = false;
            
            if (response.attributes.Status === 'Created') {
              if (isApprover) {
                actionNeeded = 'Needs Approval';
                canTakeAction = true;
              } else if (isReviewer) {
                actionNeeded = 'Waiting for Approval';
                canTakeAction = false;
              }
            } else if (response.attributes.Status === 'Approved') {
              if (isReviewer) {
                actionNeeded = 'Needs Review';
                canTakeAction = true;
              } else {
                actionNeeded = 'Waiting for Review';
                canTakeAction = false;
              }
            }
            
            return {
              responseId: response.id,
              metricId: metric.id,
              metricName: metric.attributes.Name,
              category: metric.attributes.Category,
              submittedBy: response.attributes.users_permissions_user?.data?.email || 'Unknown',
              submittedDate: response.attributes.createdAt,
              currentStatus: response.attributes.Status,
              workflowType: 'Default', // TODO: Update when new workflow system is ready
              actionNeeded,
              canTakeAction,
              userRole: isApprover ? 'Approver' : 'Reviewer',
              comments: response.attributes.Comments?.comments || [],
              progress: response.attributes.Progress || 0,
              estimatedHours: response.attributes.EstimatedHours || null,
              dueDate: response.attributes.EstimatedEndDate || null
            };
                  } catch (error) {
          // Handle error silently
          return null;
        }
        })
      );
      
      // Filter out null items and set state
      const validItems = pendingMetrics.filter(item => item !== null);
      setPendingItems(validItems);
      
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    setFiltering(true);
    
    let filtered = pendingItems || [];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.metricName?.toLowerCase().includes(searchLower) ||
          item.category?.toLowerCase().includes(searchLower) ||
          item.submittedBy?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => 
        item.currentStatus === statusFilter
      );
    }

    // Action filter
    if (actionFilter !== "all") {
      filtered = filtered.filter(item => 
        item.actionNeeded === actionFilter
      );
    }

    // Workflow filter
    if (workflowFilter !== "all") {
      filtered = filtered.filter(item => 
        true // TODO: Update when new workflow system is ready
      );
    }

    setFilteredItems(filtered);
    
    // Use setTimeout to simulate the filtering process and provide visual feedback
    setTimeout(() => {
      setFiltering(false);
    }, 300);
  };

  /**
   * Handle approval action
   */
  const handleApproval = async (isApproved) => {
    if (!selectedItem || commentText.trim() === '') {
      alert('Please provide a comment before approving/rejecting');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Determine new status based on current status
      let newStatus = '';
      
      if (isApproved) {
        newStatus = selectedItem.currentStatus === 'Created' ? 'Approved' : 'Done';
      } else {
        newStatus = selectedItem.currentStatus === 'Created' ? 'Rejected' : 'Rejected by Reviewer';
      }
      
      // Update the response status
      await UpdateOneData("responses", selectedItem.responseId, {
        Status: newStatus,
        Comments: {
          comments: [
            {
              author_id: currentUserId,
              author: userLogged.user.email,
              text: commentText,
              role: selectedItem.userRole,
              date: Date.now(),
            },
            ...(selectedItem.comments || [])
          ]
        }
      });
      
      // TODO: Implement workflow synchronization when new workflow system is ready
      
      // Close modal and refresh
      onApprovalModalClose();
      setCommentText('');
      setSelectedItem(null);
      await fetchPendingWorkflowItems();
      
      // Show success message
      alert(`Metric ${isApproved ? 'approved' : 'rejected'} successfully!`);
      
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(`Error processing approval: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Open approval modal for a specific item
   */
  const openApprovalModal = (item) => {
    setSelectedItem(item);
    setCommentText('');
    onApprovalModalOpen();
  };

  /**
   * Get status color for chips
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'Created': return 'warning';
      case 'Approved': return 'primary';
      case 'Done': return 'success';
      case 'Rejected': return 'danger';
      default: return 'default';
    }
  };

  /**
   * Get action button color
   */
  const getActionColor = (action) => {
    if (action.includes('Needs')) return 'primary';
    if (action.includes('Waiting')) return 'default';
    return 'secondary';
  };

  const getRoleColor = (role) => {
    const roleColors = {
      "Approver": "success",
      "Reviewer": "primary"
    };
    return roleColors[role] || "default";
  };

  const getRoleIcon = (role) => {
    const roleIcons = {
      "Approver": faFlag,
      "Reviewer": faCheckCircle
    };
    return roleIcons[role] || faInfoCircle;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const renderApprovalCard = (item) => {
    const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && 
                      item.currentStatus !== "Done";

    return (
      <Card 
        key={item.responseId} 
        className={`border-2 transition-all hover:shadow-md hover:border-emerald-300 cursor-pointer ${
          isOverdue ? 'border-red-200 bg-red-50' : 'border-slate-200'
        }`}
        shadow="sm"
        onClick={() => openApprovalModal(item)}
      >
        <CardBody className="p-4">
          {/* Item Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Chip
                  size="sm"
                  color={getRoleColor(item.userRole)}
                  variant="flat"
                  startContent={<FontAwesomeIcon icon={getRoleIcon(item.userRole)} className="w-3 h-3" />}
                >
                  {item.userRole}
                </Chip>
                <Chip
                  size="sm"
                  color={getStatusColor(item.currentStatus)}
                  variant="flat"
                >
                  {item.currentStatus}
                </Chip>
                <Chip
                  size="sm"
                  color={getActionColor(item.actionNeeded)}
                  variant="flat"
                >
                  {item.actionNeeded}
                </Chip>
              </div>
              
              <h3 className="font-semibold text-slate-900 text-sm mb-1">
                {item.metricName}
              </h3>
              
              <div className="text-xs text-slate-600 mb-2">
                Category: {item.category}
              </div>
              
              {/* Workflow Info */}
              <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                <span className="text-slate-500">Workflow: </span>
                <span className="font-medium text-slate-700">
                  Default
                </span>
              </div>
            </div>
            
            {isOverdue && (
              <div className="text-red-500 text-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
              <span>Progress</span>
              <span>{item.progress || 0}%</span>
            </div>
            <Progress
              size="sm"
              value={item.progress || 0}
              color="success"
              className="w-full"
            />
          </div>

          {/* Item Details */}
          <div className="space-y-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} className="w-3 h-3" />
              <span>By: {item.submittedBy}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3" />
              <span>Submitted: {formatDate(item.submittedDate)}</span>
            </div>
            
            {item.dueDate && (
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                <span>Due: {formatDate(item.dueDate)}</span>
              </div>
            )}
            
            {item.estimatedHours && (
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                <span>Est. {item.estimatedHours}h</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="flex-1 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                openApprovalModal(item);
              }}
            >
              <FontAwesomeIcon icon={faEye} className="mr-2" />
              {item.canTakeAction ? 'Review' : 'View'}
            </Button>
          </div>
          
          {/* Click hint */}
          <div className="text-xs text-slate-500 text-center mt-2 opacity-60 hover:opacity-100 transition-opacity">
            💡 Click anywhere on the card to review
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderApprovalTable = () => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Metric</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Action Needed</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Workflow</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Progress</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredItems.map((item) => {
              const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && 
                                item.currentStatus !== "Done";

              return (
                <tr 
                  key={item.responseId} 
                  className={`${isOverdue ? 'bg-red-50' : 'hover:bg-slate-50'} cursor-pointer transition-colors duration-200`}
                  onClick={() => openApprovalModal(item)}
                >
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-sm text-slate-900">{item.metricName}</div>
                      <div className="text-xs text-slate-600">{item.category}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-slate-900">{item.category}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getRoleColor(item.userRole)}
                      variant="flat"
                      startContent={<FontAwesomeIcon icon={getRoleIcon(item.userRole)} className="w-3 h-3" />}
                    >
                      {item.userRole}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getStatusColor(item.currentStatus)}
                      variant="flat"
                    >
                      {item.currentStatus}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getActionColor(item.actionNeeded)}
                      variant="flat"
                    >
                      {item.actionNeeded}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <Chip size="sm" variant="flat">
                      Default
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress
                        size="sm"
                        value={item.progress || 0}
                        color="success"
                        className="w-16"
                      />
                      <span className="text-xs text-slate-600">{item.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="flat" 
                        color="primary" 
                        className="text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          openApprovalModal(item);
                        }}
                      >
                        {item.canTakeAction ? 'Review' : 'View'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header Skeleton */}
          <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200 mb-6">
            <div className="py-4">
              <div className="h-8 bg-slate-200 rounded-lg w-1/3 mb-2 animate-pulse"></div>
              <div className="h-4 bg-slate-200 rounded-lg w-1/2 animate-pulse"></div>
            </div>
          </div>
          
          {/* Breadcrumbs Skeleton */}
          <div className="mb-6">
            <div className="h-4 bg-slate-200 rounded-lg w-1/4 animate-pulse"></div>
          </div>
          
          {/* Content Loading */}
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-6"></div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Loading approval dashboard...</h3>
            <p className="text-slate-600 mb-4">Please wait while we fetch your pending approvals and reviews.</p>
            <div className="flex justify-center gap-2">
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce"></div>
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Approval Dashboard</h1>
                <p className="text-slate-600 mt-1">Review and approve metrics that require your attention</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-2">
        <BreadCrumbsPage />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col gap-6">
          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" 
                  />
                  <input
                    type="text"
                    placeholder="Search metrics by name, category, or submitter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {searchTerm && filtering && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Filters */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Statuses</option>
                  <option value="Created">Created</option>
                  <option value="Approved">Approved</option>
                  <option value="Done">Done</option>
                  <option value="Rejected">Rejected</option>
                </select>

                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Actions</option>
                  <option value="Needs Approval">Needs Approval</option>
                  <option value="Needs Review">Needs Review</option>
                  <option value="Waiting for Approval">Waiting for Approval</option>
                  <option value="Waiting for Review">Waiting for Review</option>
                </select>

                <select
                  value={workflowFilter}
                  onChange={(e) => setWorkflowFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Workflows</option>
                  <option value="Default">Default</option>
                  <option value="2-Step Approval">2-Step Approval</option>
                </select>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  <Button
                    size="sm"
                    variant={layoutMode === "cards" ? "solid" : "light"}
                    color={layoutMode === "cards" ? "success" : "default"}
                    onClick={() => setLayoutMode("cards")}
                    className="px-3 py-1 text-xs"
                    disabled={filtering}
                  >
                    <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 mr-1" />
                    Cards
                  </Button>
                  <Button
                    size="sm"
                    variant={layoutMode === "table" ? "solid" : "light"}
                    color={layoutMode === "table" ? "success" : "default"}
                    onClick={() => setLayoutMode("table")}
                    className="px-3 py-1 text-xs"
                    disabled={filtering}
                  >
                    <FontAwesomeIcon icon={faTable} className="w-3 h-3 mr-1" />
                    Table
                  </Button>
                </div>

                {/* Refresh Button */}
                <Button
                  variant="flat"
                  startContent={isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                  ) : (
                    <FontAwesomeIcon icon={faRefresh} />
                  )}
                  onClick={fetchPendingWorkflowItems}
                  className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  disabled={isLoading}
                >
                  {isLoading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </div>
          </div>

          {/* Content Display */}
          {isLoading ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading your approvals...</h3>
                  <p className="text-slate-600">Please wait while we fetch your pending approvals and reviews.</p>
                </div>
              </CardBody>
            </Card>
          ) : filtering ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Applying filters...</h3>
                  <p className="text-slate-600">Please wait while we filter your approvals.</p>
                </div>
              </CardBody>
            </Card>
          ) : filteredItems.length === 0 ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No pending approvals</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm || statusFilter !== "all" || actionFilter !== "all" || workflowFilter !== "all"
                      ? "Try adjusting your filters or search terms."
                      : "All metrics have been processed or are waiting for other users."}
                  </p>
                  {(searchTerm || statusFilter !== "all" || actionFilter !== "all" || workflowFilter !== "all") && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("all");
                        setActionFilter("all");
                        setWorkflowFilter("all");
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Results Summary */}
              <div className="text-sm text-slate-600 mb-4 flex items-center gap-2">
                <span>Showing {filteredItems.length} of {pendingItems.length} pending approvals</span>
                {filtering && (
                  <div className="flex items-center gap-1 text-emerald-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600"></div>
                    <span className="text-xs">Filtering...</span>
                  </div>
                )}
              </div>
              
              {layoutMode === "cards" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredItems.map(renderApprovalCard)}
                </div>
              ) : (
                renderApprovalTable()
              )}
            </>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      <Modal isOpen={isApprovalModalOpen} onClose={onApprovalModalClose} size="2xl">
        <ModalContent>
          <ModalHeader>
            <h3 className="text-lg font-semibold">
              Review Metric: {selectedItem?.metricName}
            </h3>
          </ModalHeader>
          <ModalBody>
            {selectedItem && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Category:</span> {selectedItem.category}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span> {selectedItem.currentStatus}
                  </div>
                  <div>
                    <span className="font-medium">Submitted By:</span> {selectedItem.submittedBy}
                  </div>
                  <div>
                    <span className="font-medium">Current Status:</span> {selectedItem.currentStatus}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Comment (Required)
                  </label>
                  <Textarea
                    placeholder="Provide your feedback, approval reasoning, or rejection comments..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    minRows={3}
                    isRequired
                  />
                </div>
                
                {selectedItem.comments && selectedItem.comments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Previous Comments</h4>
                    <div className="bg-gray-50 p-3 rounded-md max-h-32 overflow-y-auto">
                      {selectedItem.comments.map((comment, index) => (
                        <div key={index} className="text-sm mb-2">
                          <span className="font-medium">{comment.author}</span>
                          <span className="text-gray-500 ml-2">({comment.role})</span>
                          <div className="text-gray-700 mt-1">{comment.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="flat"
              onPress={() => handleApproval(false)}
              isDisabled={isProcessing || commentText.trim() === ''}
              isLoading={isProcessing}
            >
              <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
              Reject
            </Button>
            <Button
              color="success"
              onPress={() => handleApproval(true)}
              isDisabled={isProcessing || commentText.trim() === ''}
              isLoading={isProcessing}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              Approve
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ApprovalDashboard;
